import React, { useState, useEffect, useContext, useRef } from "react";
import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import Button from "../../components/Button";
import AppLogo from "../../components/AppLogo/AppLogo";
import FormItem from "../../components/FormItem";
import InputErrorMessage from "../../components/InputErrorMessage";
import InputField from "../../components/InputField";
import ActivationSkeleton from "../Skeletons/ActivationSkeleton";

import { generateRequestConfig, cookieDomain, cookieSecure, cookieSameSite} from "../../constants";
import { AuthContext } from "../../contexts/AuthContext";
import useTimer from "../../hooks/useTimer";
import {
  getAppInfo,
  handleErrorForms,
  getEmailFromParams,
  getDeviceId,
  ensureCsrfToken,
} from "../../utils";
import {
  activationFields,
  getCodeErrorMessages,
  ActivationFormDataType,
} from "./Activation.constants";

import { ActivationFormText, HttpMethod, Endpoint } from "../../interfaces";

type ActivationState = {
  code: string;
  countdown: number;
  generalErrors?: string;
  resendButton: boolean;
  submitButton: boolean;
  appInfo: any | null;
};

const Activation: React.FC = () => {
  type FormField = "code";

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const userEmail = getEmailFromParams();
  const params = new URLSearchParams(window.location.search);

  const { handleLogIn, AuthContextState } = useContext(AuthContext);
  const { csrf_token, idApp, referer } = AuthContextState;

  const [state, setState] = useState<ActivationState>({
    code: "",
    countdown: 60,
    generalErrors: undefined,
    resendButton: false,
    submitButton: false,
    appInfo: null,
  });

  const { resendButton, submitButton, generalErrors, appInfo, countdown } = state;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { register, handleSubmit, formState: { errors }, setError, clearErrors } = useForm<ActivationFormDataType>();
  const isLoaded = useTimer(600);

  useEffect(() => {
    document.title = t("Activation");

    if (!userEmail) {
      navigate(`/${currentLang}/registration/?appId=${idApp}&referer=${referer}`);
    }

    if (!appInfo) {
      getAppInfo((data) => setState(prev => ({ ...prev, appInfo: data })));
    }

    const blockedUntil = Cookies.get("blockedUntil");
    if (blockedUntil) {
      const remainingTime = Math.floor((parseInt(blockedUntil, 10) - Date.now()) / 1000);
      if (remainingTime > 0) {
        startCountdown(remainingTime);
      }
    }

    if (params.has('confirm')) {
      setFormText({
        formTitle: "Confirm your account",
        buttonTitle: "Confirm",
      });
    }
  }, [t, i18n]);

  // Очистка таймера при размонтировании компонента
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const startCountdown = (initialSeconds: number) => {
    if (initialSeconds <= 0) {
      setState(prev => ({ ...prev, resendButton: false, countdown: 0 }));
      return;
    }

    setState(prev => ({ ...prev, resendButton: true, countdown: initialSeconds }));

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setState(prev => {
        if (prev.countdown <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          return { ...prev, resendButton: false, countdown: 0, generalErrors: undefined };
        }
        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1000);
  };

  const handleResendCode = async () => {
    clearErrors();
    setState(prev => ({ ...prev, generalErrors: undefined }));
    const csrfToken = await ensureCsrfToken(csrf_token);

    const isConfirmation = params.has('confirm') ? "confirmation" : "activation";

    const requestConfig = generateRequestConfig(
      "put",
      "resendcode",
      {
        email: userEmail,
        appId: idApp,
        confirmation: isConfirmation,
      },
      {
        "X-CSRF-TOKEN": csrfToken,
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Accept-Language": currentLang,
      }
    );

    try {
      const res = await axios(requestConfig);

      if (res.data.message) {
        setState(prev => ({ ...prev, generalErrors: res.data.message }));
      }

      if (res.data.remaining_time) {
        const remainingTime = res.data.remaining_time;
        setState(prev => ({ ...prev, countdown: remainingTime }));
        startCountdown(remainingTime);

        const blockedUntil = Date.now() + remainingTime * 1000;
        Cookies.set("blockedUntil", blockedUntil.toString(), {
          expires: new Date(blockedUntil + remainingTime * 1000),
        });
      }

      if (res.data.errors?.code) {
        setError("code", { type: "server", message: res.data.errors.code });
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const remainingTime = error.response?.data?.remaining_time;
        if (remainingTime) {
          setState(prev => ({ ...prev, countdown: remainingTime }));
          startCountdown(remainingTime);

          const blockedUntil = Date.now() + remainingTime * 1000;
          Cookies.set("blockedUntil", blockedUntil.toString(), {
            expires: new Date(blockedUntil + remainingTime * 1000),
          });
        }
      }

      const errorAction = handleErrorForms(error);
      if (errorAction) {
        if (errorAction.field) {
          setError(errorAction.field as FormField, { type: "server", message: errorAction.message });
        } else {
          setState(prev => ({ ...prev, generalErrors: errorAction.message }));
        }
      }
    }
  };

  interface CountdownProps {
    countdown: number;
  }

  const CountdownDisplay: React.FC<CountdownProps> = ({ countdown }) => {
    const minutes = Math.floor(countdown / 60);
    const seconds = countdown % 60;

    return (
      <div role="status" aria-live="polite" aria-atomic="true">
        {minutes > 0 ? (
          <>
            {t("Resend in")} {minutes}:{seconds < 10 ? `0${seconds}` : seconds} {t("minutes")}
          </>
        ) : (
          <>
            {t("Resend in")} {seconds} {t("seconds")}
          </>
        )}
      </div>
    );
  };

  const handleSubmitActivation: SubmitHandler<ActivationFormDataType> = async (data) => {
    setState(prev => ({ ...prev, generalErrors: undefined }));
    const httpMethod: HttpMethod = params.has('confirm') ? "put" : "put";
    const endPoint: Endpoint = params.has('confirm') ? "confirmation" : "activation";

    const requestConfig = generateRequestConfig(
      httpMethod,
      endPoint,
      {
        email: userEmail,
        appId: idApp,
        deviceId: getDeviceId(),
        ...data,
      },
      {
        "X-CSRF-TOKEN": csrf_token,
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Accept-Language": currentLang,
      }
    );

    try {
      const res = await axios(requestConfig);

      if (res.status === 202) {
        const expirationDate = new Date(res.data.resetpassword_accessTokenExpAt);
        Cookies.set('x-reset-password-token', res.data.resetpassword_accessToken, { 
          expires: expirationDate, 
          secure: cookieSecure, 
          domain: cookieDomain, 
          sameSite: cookieSameSite
         });
        navigate(`/${currentLang}/change-password/?appId=${idApp}`);
      }

      if (res.status === 200) {
        handleLogIn(res.data);
      }
    } catch (error) {
      const errorAction = handleErrorForms(error);

      if (errorAction) {
        if (errorAction.field) {
          setError(errorAction.field as FormField, { type: "server", message: errorAction.message });
        } else {
          setState(prev => ({ ...prev, generalErrors: errorAction.message }));
        }
      }
    }
  };

  const [formText, setFormText] = useState<ActivationFormText>({
    formTitle: "Activate the account",
    buttonTitle: "Activate",
  });

  const codeErrorMessages = getCodeErrorMessages(errors);

  return (
    <>
      {!isLoaded ? (
        <ActivationSkeleton />
      ) : (
        <>
          <h1 className="text-center auth-title-form">
            {t(formText.formTitle)}
            <AppLogo
              src={appInfo?.urlImage}
              name={appInfo?.name}
              frameClassName="auth-title-logo-frame"
              imageClassName="auth-title-logo"
              placeholderClassName="auth-title-logo-placeholder"
            />
          </h1>
          <div className="card-body">
            <form method="POST" onSubmit={handleSubmit(handleSubmitActivation)}>
              <input type="hidden" name="email" value={userEmail || ""} />

              <FormItem
                label={t(activationFields.code.label)}
                labelFor={activationFields.code.id}
              >
                <InputField
                  autoFocus
                  id={activationFields.code.id}
                  type={activationFields.code.type}
                  placeholder={t(activationFields.code.placeholder)}
                  {...register(
                    activationFields.code.name,
                    activationFields.code.validation
                  )}
                  className={generalErrors || errors.code ? "error" : undefined}
                />
                {errors.code && (
                  <InputErrorMessage
                    msg={t(codeErrorMessages[errors.code.type])}
                  />
                )}
                {generalErrors && <InputErrorMessage msg={t(generalErrors)} />}

                <div className="optext w-100 pb-3">
                  <button
                    type="button"
                    className={`btn p-0 d-under ${resendButton ? "d-none" : ""}`}
                    onClick={handleResendCode}
                  >
                    {t("Re-send new code")}!
                  </button>
                  {resendButton && <CountdownDisplay countdown={countdown} />}
                </div>
              </FormItem>

              <FormItem>
                <Button
                  type="submit"
                  variant="default"
                  width={100}
                  height={48}
                  disabled={submitButton}
                  text={t(formText.buttonTitle)}
                />
              </FormItem>

              <div className="p-3 text-center pb-5">
                {t("Already have an account?")}{" "}
                <Link
                  to={`/${currentLang}/login?appId=${idApp}&referer=${referer}`}
                  className="fw-bold"
                >
                  {t("Login")}
                </Link>
              </div>
            </form>
          </div>
        </>
      )}
    </>
  );
};

export default Activation;
